import { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import Icon from "@mui/material/Icon"
import Grid from "@mui/material/Grid"
import DashboardLayout from "examples/LayoutContainers/DashboardLayout"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"
import SectionCard from "./components/SectionCard"
import StatusBadge from "./components/StatusBadge"
import { useGetPurchaseQuery, useCancelPurchaseMutation } from "shared/redux/endpoints/procurementPurchases"
import { PurchaseConfirmModal } from "./components/purchase/PurchaseActionBar"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { useGetPurchasePaymentContextQuery } from "shared/redux/endpoints/procurementPayments"
import { formatVND, formatDate } from "./constants"

const Field = ({ label, value, full, strong }) => (
  <Grid item xs={12} sm={full ? 12 : 6}>
    <MDTypography variant="caption" color="text" display="block">
      {label}
    </MDTypography>
    <MDTypography variant={strong ? "h6" : "button"} fontWeight={strong ? "bold" : "medium"} sx={{ mt: 0.25, display: "block" }}>
      {value ?? "—"}
    </MDTypography>
  </Grid>
)

// F2 — Purchase Payment Context. What a Payment Request is created against:
// the Purchase's supplier + default bank, its total, every batch already
// created for it, and what's left to pay. `remaining` gates whether "Create
// Payment Request" is even offered.
const PurchasePaymentContextContainer = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: po, isLoading, isError, refetch } = useGetPurchaseQuery(id, { skip: !id })

  const [cancelPurchase, { isLoading: cancelling }] = useCancelPurchaseMutation()
  const [cancelOpen, setCancelOpen] = useState(false)

  const handleCancel = async (reason) => {
    try {
      await cancelPurchase({ id, reason }).unwrap()
      showToastSuccess("The PO has been cancelled.")
      setCancelOpen(false)
      refetch()
    } catch (e) {
      showToastError(
        e?.data?.data?.message || e?.data?.message || "Couldn't cancel this PO. Please try again."
      )
    }
  }

  const purchaseArg = po
    ? {
        id: po.id ?? id,
        poCode: po.poCode,
        total: po.total,
        supplierId: po.vendorId ?? po.supplierId,
        supplierName: po.supplierName,
      }
    : undefined
  const { data: ctx, isFetching: ctxLoading } = useGetPurchasePaymentContextQuery(purchaseArg, { skip: !purchaseArg })

  const back = () => navigate("/procurement/to-pay")

  const BackButton = (
    <MDButton variant="text" color="info" size="small" onClick={back}>
      <Icon sx={{ mr: 0.5 }}>arrow_back</Icon>
      Back to To Pay
    </MDButton>
  )

  if (isLoading || (po && ctxLoading && !ctx)) {
    return (
      <DashboardLayout>
        <MDBox p={3}>
          <MDBox mb={2}>{BackButton}</MDBox>
          <MDTypography variant="body2" color="text">
            Loading…
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    )
  }

  if (isError || !po) {
    return (
      <DashboardLayout>
        <MDBox p={3}>
          <MDBox mb={2}>{BackButton}</MDBox>
          <SectionCard mb={0}>
            <MDBox textAlign="center" py={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={1}>
                Couldn&apos;t load this purchase
              </MDTypography>
              <MDButton variant="outlined" color="info" size="small" onClick={refetch}>
                Retry
              </MDButton>
            </MDBox>
          </SectionCard>
        </MDBox>
      </DashboardLayout>
    )
  }

  const remaining = ctx?.remainingToPay ?? po.total
  const paidBatches = ctx?.paidBatches || []
  // A new payment can always be created here — the backend no longer blocks
  // one that would push the PO over its remaining balance (2026-09
  // barrier-removal follow-up), it just gets flagged as over afterward.

  return (
    <DashboardLayout>
      <MDBox p={3}>
        <MDBox mb={2}>{BackButton}</MDBox>

        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1.5} mb={3}>
          <MDBox>
            <MDTypography variant="h4">{po.poCode || "Purchase"}</MDTypography>
            <MDTypography variant="caption" color="text">
              Procurement › To Pay › {po.poCode || id}
            </MDTypography>
          </MDBox>
          <StatusBadge status={po.status} />
        </MDBox>

        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} lg={8}>
            <SectionCard icon="storefront" title="Supplier & default bank" subtitle={po.supplierName}>
              <Grid container spacing={2}>
                <Field label="Supplier" value={po.supplierName} />
                <Field label="Category" value={po.categoryName} />
                <Field label="Region" value={po.region} />
                <Field label="Total amount" value={formatVND(po.total)} />
                <Field label="Default receiver" value={ctx?.bankDefault?.bankReceiverName} />
                <Field label="Default bank / branch" value={ctx?.bankDefault?.bankNameBranch} />
                <Field label="Default account" value={ctx?.bankDefault?.bankAccount} full />
              </Grid>
            </SectionCard>

            <SectionCard icon="receipt_long" title="Payment Requests" subtitle={`${paidBatches.length} payment request(s) so far`}>
              {paidBatches.length === 0 ? (
                <MDTypography variant="body2" color="text">
                  No payment requests created for this purchase yet.
                </MDTypography>
              ) : (
                <MDBox display="flex" flexDirection="column" gap={1}>
                  {paidBatches.map((b) => (
                    <MDBox
                      key={b.paymentRequestId}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                      p={1.5}
                      borderRadius="lg"
                      bgColor="grey-100"
                    >
                      <MDBox minWidth={0}>
                        <MDTypography variant="button" fontWeight="medium">
                          {b.batchLabel}
                        </MDTypography>
                        <MDTypography variant="caption" color="text" display="block">
                          {b.paymentCode} · {formatDate(b.submittedAt)}
                        </MDTypography>
                      </MDBox>
                      <MDBox display="flex" alignItems="center" gap={1.5}>
                        <MDTypography variant="button" fontWeight="bold">
                          {formatVND(b.allocatedAmount)}
                        </MDTypography>
                        <StatusBadge status={b.status} />
                        <MDButton
                          size="small"
                          variant="text"
                          color="info"
                          onClick={() => navigate(`/procurement/payment-requests/${b.paymentRequestId}`)}
                        >
                          View
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  ))}
                </MDBox>
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={4}>
            <MDBox sx={{ position: { lg: "sticky" }, top: { lg: 24 } }}>
              <SectionCard icon="payments" title="Remaining to pay" mb={0}>
                <MDBox p={1.5} borderRadius="lg" bgColor="grey-100" mb={2}>
                  <MDTypography variant="caption" color="text" display="block">
                    Remaining balance
                  </MDTypography>
                  <MDTypography
                    variant="h5"
                    fontWeight="bold"
                    color={remaining > 0 ? "warning" : remaining < 0 ? "error" : "success"}
                  >
                    {formatVND(remaining)}
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    of {formatVND(po.total)} total
                  </MDTypography>
                </MDBox>
                <MDButton
                  fullWidth
                  variant="gradient"
                  color="info"
                  onClick={() => navigate(`/procurement/payment-requests/new?purchaseId=${po.id ?? id}`)}
                >
                  <Icon sx={{ mr: 0.75 }}>add</Icon>
                  Create Payment Request
                </MDButton>
                {(po.allowedActions || []).includes("cancel") && (
                  <MDButton fullWidth variant="outlined" color="error" sx={{ mt: 1.5 }} onClick={() => setCancelOpen(true)}>
                    <Icon sx={{ mr: 0.75 }}>block</Icon>
                    Cancel PO
                  </MDButton>
                )}
                {remaining <= 0 && (
                  <MDTypography variant="caption" color="text" display="block" textAlign="center" mt={1}>
                    {remaining < 0
                      ? "This PO is already over its budget — a new payment will add to that."
                      : "Fully paid — a new payment here will be flagged as over the PO amount."}
                  </MDTypography>
                )}
              </SectionCard>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>

      {cancelOpen && (
        <PurchaseConfirmModal
          actionKey="cancel"
          po={po}
          busy={cancelling}
          onCancel={() => !cancelling && setCancelOpen(false)}
          onSubmit={handleCancel}
        />
      )}
    </DashboardLayout>
  )
}

export default PurchasePaymentContextContainer
